import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { auth } from "../firebase";
import {
    normalizeTheme,
    readStoredTheme,
    writeStoredTheme,
    applyThemeAttribute,
} from "../utils/theme";

const BACKEND = "https://bluebirddocumentationadmin.pythonanywhere.com";

const ThemeContext = createContext(null);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};

/**
 * Single owner of the app theme (BBD-77 / BBD-78 / BBD-81).
 *
 *  - personalTheme: the signed-in user's choice. Cached in localStorage under
 *    "theme" (pre-paint bootstrap in public/index.html reads the same key) and
 *    persisted to User.theme on the backend, which is the source of truth.
 *  - workspaceTheme: a route-scoped override set by team-workspace pages.
 *    While set it wins; clearing it restores the personal theme.
 *
 * The provider applies the theme by setting data-theme on <html>. Nothing else
 * in the app may touch theme CSS variables or localStorage("theme").
 */
export const ThemeProvider = ({ children }) => {
    const [personalTheme, setPersonalTheme] = useState(readStoredTheme);
    const [workspaceTheme, setWorkspaceThemeState] = useState(null);
    const [canUseThemes, setCanUseThemes] = useState(null); // null = unknown until user loads
    const [syncedUid, setSyncedUid] = useState(null);
    const personalRef = useRef(personalTheme);
    personalRef.current = personalTheme;

    const theme = workspaceTheme || personalTheme;

    // Apply before paint so a theme change never flashes the previous look.
    useLayoutEffect(() => {
        applyThemeAttribute(theme);
    }, [theme]);

    const persistTheme = useCallback(async (nextTheme) => {
        const user = auth.currentUser;
        if (!user) return { ok: true, offline: true };
        const token = await user.getIdToken();
        const response = await fetch(`${BACKEND}/users/update-theme/`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "auth-token": `Bearer ${token}`,
                "Authorization": `${token}`,
            },
            body: JSON.stringify({ theme: nextTheme }),
        });
        let data = {};
        try { data = await response.json(); } catch (e) { /* no body */ }
        return { ok: response.ok, status: response.status, theme: data.theme, error: data.error };
    }, []);

    /**
     * Set the personal theme. Applies instantly, caches locally, and persists
     * to the backend. If the backend rejects it (tier gating) the theme snaps
     * back to Default and the rejection is returned to the caller.
     */
    const setTheme = useCallback(async (value, { persist = true } = {}) => {
        const next = normalizeTheme(value);
        const previous = personalRef.current;
        setPersonalTheme(next);
        writeStoredTheme(next);
        if (!persist) return { ok: true, theme: next };
        try {
            const result = await persistTheme(next);
            if (!result.ok) {
                const fallback = result.status === 403 ? "default" : previous;
                setPersonalTheme(fallback);
                writeStoredTheme(fallback);
                return { ok: false, theme: fallback, error: result.error || "Theme not saved" };
            }
            if (result.theme && normalizeTheme(result.theme) !== next) {
                // Server normalized it differently (legacy value) — follow the server.
                setPersonalTheme(normalizeTheme(result.theme));
                writeStoredTheme(result.theme);
            }
            return { ok: true, theme: next };
        } catch (error) {
            console.error("Failed to persist theme:", error);
            return { ok: true, theme: next, offline: true };
        }
    }, [persistTheme]);

    const setWorkspaceTheme = useCallback((value) => {
        setWorkspaceThemeState(value ? normalizeTheme(value) : null);
    }, []);

    const clearWorkspaceTheme = useCallback(() => {
        setWorkspaceThemeState(null);
    }, []);

    // Login sync: the backend value wins over the local cache. Accounts without
    // theme access are forced to Default here (and server-side), which replaces
    // the old checkSubscriberStatus() localStorage hack that also wiped VIP themes.
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (!user) {
                setCanUseThemes(null);
                setSyncedUid(null);
                return;
            }
            try {
                const token = await user.getIdToken();
                const response = await fetch(`${BACKEND}/users/${user.uid}/`, {
                    headers: { "Authorization": `${token}` },
                });
                if (!response.ok) return;
                const data = await response.json();
                // Any paid storage plan or VIP unlocks themes (BBD-99). Fall back to
                // the old rule until the backend exposing has_theme_access is deployed.
                const eligible = typeof data.has_theme_access === "boolean"
                    ? data.has_theme_access
                    : Boolean(data.is_subscriber || data.is_vip);
                setCanUseThemes(eligible);

                const serverTheme = data.theme ? normalizeTheme(data.theme) : null;
                const localTheme = personalRef.current;

                if (!eligible) {
                    setPersonalTheme("default");
                    writeStoredTheme("default");
                } else if (serverTheme) {
                    setPersonalTheme(serverTheme);
                    writeStoredTheme(serverTheme);
                } else if (localTheme !== "default") {
                    // Never persisted before this rework: adopt the local choice once.
                    persistTheme(localTheme).catch(() => {});
                }
            } catch (error) {
                console.error("Theme sync failed:", error);
            } finally {
                setSyncedUid(user.uid);
            }
        });
        return () => unsubscribe();
    }, [persistTheme]);

    const value = useMemo(() => ({
        theme,
        personalTheme,
        workspaceTheme,
        canUseThemes,
        isSynced: syncedUid !== null,
        setTheme,
        setWorkspaceTheme,
        clearWorkspaceTheme,
    }), [theme, personalTheme, workspaceTheme, canUseThemes, syncedUid, setTheme, setWorkspaceTheme, clearWorkspaceTheme]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};
